import { MinusIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { Textarea } from '@/shared/components/ui/textarea';
import { AddonsList, AddonsItem } from '@/modules/stores/components';
import { Quantity } from '@/shared/components/ui/quantity';
import { formatPrice } from '@/shared/utils/format-price';
import { getImageUrl } from '@/shared/utils/image';
import { getAddonQuantity, isAddonSelected, getMaxForAddon } from '@/modules/stores/utils/addon';
import { getDiscountPercent, getPromotion } from '@/modules/stores/utils/promotion';
import { getDisplayPrice } from '@/modules/stores/utils/display-price';
import { PromotionBadge, PromotionPrice } from '@/modules/stores/components/promotion-price';
import { QuantityPromotionNote } from '@/modules/stores/components/quantity-promotion-chip';
import { getFixedComboItems } from '@/modules/stores/utils/combo';
import { ComboWizard } from './combo-wizard';
import { WholesaleQuantityPicker } from './wholesale-quantity-picker';
import { useProductDrawerModel } from './use-product-drawer-model';

export function ProductDrawerContent(props: ReturnType<typeof useProductDrawerModel>) {
  const {
    product,
    totalPrice,
    quantityPromotionNote,
    wholesaleFamily,
    wholesaleUnitsInCart,
    wholesaleTotalPrice,
    handleSetQuantity,
    handleAdvanceToDrawer,
    handleIncrementQuantity,
    handleDecrementQuantity,
    updateObservations,
    watch,
    handleAddonChange,
    selectedAddons,
    filteredAddonCategories,
    addonSearchTerm,
    handleAddonSearchChange,
    animatingBadge,
    handleIncrementAddon,
    handleToggleRadioAddon,
    isDescriptionExpanded,
    handleToggleDescription,
    isMandatoryCategoryComplete,
    isEditMode,
    isClubRedeem,
    clubRedeemPoints,
    scrollContainerRef,
  } = props;

  if (!product) {
    return (
      <div className="flex-1 flex items-center justify-center rounded-t-2xl">
        <p className="text-gray-500">Produto não encontrado</p>
      </div>
    );
  }

  // Combo de escolha (PRAT-20): montagem em etapas, com resumo no fim.
  if (props.isComboWizard) return <ComboWizard {...props} />;

  // Preço de exibição centralizado (regra De/Por + piso de adicionais
  // obrigatórios), reutilizando o mesmo helper dos cards de produto para manter
  // a apresentação de preço consistente em toda a jornada.
  const { price, regularPrice, hasRequiredAddons } = getDisplayPrice(product);
  const fixedComboItems = getFixedComboItems(product);

  return (
    <div className="flex flex-col h-full bg-white rounded-t-[32px]">
      {/** Content com scroll */}
      <div className="flex-1 overflow-y-auto" data-scroll-container ref={scrollContainerRef}>
        <div className="w-full aspect-[10/7] flex z-10 rounded-t-[32px]">
          <img
            src={getImageUrl(product?.image)}
            alt={product.name}
            className="w-full h-full object-cover rounded-t-[32px]"
            onError={e => {
              e.currentTarget.src = '/fallback.svg';
            }}
          />
        </div>
        <div className="bg-white rounded-t-3xl -translate-y-5 w-full p-6 pb-0 space-y-3">
          {/** Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="font-bold text-2xl">{product.name}</h1>
              {getPromotion(product) && (
                <PromotionBadge percent={getDiscountPercent(product)} className="self-start" />
              )}
              {/* PRAT-184: este produto participa da promoção por quantidade */}
              {quantityPromotionNote && <QuantityPromotionNote note={quantityPromotionNote} />}
            </div>
            <PromotionPrice
              align="right"
              price={price}
              regularPrice={regularPrice}
              prefix={hasRequiredAddons ? 'A partir de' : undefined}
              priceClassName="text-primary text-xl"
            />
          </div>

          {/* PRAT-187: atacado por faixa — quantidade grande sem tocar no + várias vezes */}
          {wholesaleFamily && !isClubRedeem && (
            <WholesaleQuantityPicker
              family={wholesaleFamily}
              quantity={watch('quantity') || 1}
              unitsInCart={wholesaleUnitsInCart}
              basePrice={totalPrice / (watch('quantity') || 1)}
              onSetQuantity={handleSetQuantity}
            />
          )}

          {/** Description */}
          <div className="space-y-2">
            <p
              className={`text-gray-500 text-sm transition-all duration-300 ${
                isDescriptionExpanded ? '' : 'overflow-hidden text-ellipsis line-clamp-3'
              }`}
            >
              {product.description || ''}
            </p>
            {product.description && product.description.length > 100 && (
              <button
                onClick={handleToggleDescription}
                className="text-primary hover:text-primary/80 font-semibold text-sm transition-colors"
              >
                {isDescriptionExpanded ? 'Ver menos' : 'Ver mais'}
              </button>
            )}
          </div>

          {/** Combo fixo (PRAT-79): o que sempre vem no combo */}
          {fixedComboItems.length > 0 && (
            <div className="rounded-2xl bg-gray-50 p-4 space-y-2" data-testid="combo-fixed-items">
              <p className="font-semibold text-sm text-gray-800">Neste combo</p>
              <ul className="space-y-1.5">
                {fixedComboItems.map(row => (
                  <li key={row.item_id} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="font-semibold text-primary min-w-7">{row.quantity}x</span>
                    <span>{row.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/** Input de pesquisa customizado - mostrado apenas quando há addons */}
          {product.addon_categories?.length > 0 && (
            <div className="flex items-center w-full rounded-2xl border border-gray-400 py-3.5 px-5 bg-white">
              <input
                type="text"
                placeholder="Pesquisar"
                value={addonSearchTerm}
                onChange={e => handleAddonSearchChange(e.target.value)}
                className="flex-1 border-none bg-none outline-none shadow-none bg-transparent text-gray-700 placeholder:text-gray-400 text-xs placeholder:text-xs"
              />
              <SearchIcon className="w-3.5 h-3.5 text-gray-400 ml-3" />
            </div>
          )}
        </div>
        {/** Addons */}
        {filteredAddonCategories.length > 0 && (
          <div className="w-full max-w-full">
            {/** Lista de addons filtrados */}
            <AddonsList
              addonCategories={filteredAddonCategories}
              animatingBadge={animatingBadge}
              isMandatoryCategoryComplete={isMandatoryCategoryComplete}
              render={(addon, categoryId, selectMax) => {
                const maxForThisAddon = getMaxForAddon(
                  addon.id,
                  categoryId,
                  selectMax,
                  selectedAddons,
                  product.addon_categories ?? []
                );

                return (
                  <div className="flex flex-1 justify-between gap-3 w-full py-2 cursor-pointer hover:bg-gray-50">
                    <div
                      className="flex-1"
                      onClick={() =>
                        selectMax === 1
                          ? handleToggleRadioAddon(addon.id, categoryId)
                          : handleIncrementAddon(addon.id, categoryId, maxForThisAddon)
                      }
                    >
                      <AddonsItem addon={addon} />
                    </div>
                    {selectMax === 1 ? (
                      <input
                        type="radio"
                        id={`${categoryId}-${addon.id}`}
                        name={`addon-${categoryId}`}
                        value={addon.id}
                        checked={isAddonSelected(addon.id, selectedAddons)}
                        onChange={() => handleToggleRadioAddon(addon.id, categoryId)}
                        // Inputs de rádio nativos não disparam `change` ao reclicar a
                        // opção já marcada, então clicar repetidamente no mesmo
                        // complemento não atualizava o estado (faltava re-render).
                        // O `onClick` cobre esse caso, desmarcando o item já
                        // selecionado para refletir o toggle na mesma linha.
                        onClick={() => {
                          if (isAddonSelected(addon.id, selectedAddons)) {
                            handleToggleRadioAddon(addon.id, categoryId);
                          }
                        }}
                        className="shrink-0 self-center accent-primary"
                      />
                    ) : (
                      <Quantity
                        value={getAddonQuantity(addon.id, selectedAddons)}
                        max={maxForThisAddon}
                        onValueChange={value =>
                          handleAddonChange(addon.id, value, categoryId, selectMax)
                        }
                        className="w-10 h-10 self-center"
                      />
                    )}
                  </div>
                );
              }}
            />
          </div>
        )}

        {/** Mensagem quando não há resultados de busca */}
        {addonSearchTerm && filteredAddonCategories.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4 w-full">
            <SearchIcon className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum addon encontrado</h3>
            <p className="text-gray-500 text-sm">
              Não encontramos addons para "{addonSearchTerm}". Tente buscar por outro termo.
            </p>
          </div>
        )}

        {/** Observações do produto */}
        <div
          className="flex flex-col items-center justify-center w-full gap-4 pb-6"
          data-observations-section
        >
          <div className="flex items-center justify-between h-16 bg-muted w-full px-4 rounded-none">
            <p className="text-night text-sm font-bold">Observações</p>
          </div>

          <div className="w-full px-4 pb-4">
            <Textarea
              placeholder="Ex: Tirar calabresa, ovo, batata palha."
              value={watch('observations')}
              onChange={e => updateObservations(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Botões fixos no fundo do drawer */}
      <div className="bg-white border-t py-4 px-4 flex-shrink-0 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
        <div className="flex items-center gap-3">
          {/* Controle de quantidade (no atacado, o seletor fica no corpo) */}
          <div
            className={`flex items-center border border-gray-300 rounded-lg ${
              wholesaleFamily && !isClubRedeem ? 'hidden' : ''
            }`}
          >
            <button
              onClick={handleDecrementQuantity}
              className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
              disabled={watch('quantity') <= 1}
            >
              <MinusIcon className="w-5 h-5" />
            </button>
            <span className="w-10 h-10 flex items-center justify-center text-base font-semibold border-x border-gray-300">
              {watch('quantity')}
            </span>
            <button
              onClick={handleIncrementQuantity}
              className="w-10 h-10 flex items-center justify-center text-primary hover:bg-gray-50 transition-colors clickable-small"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Botão Adicionar/Salvar */}
          <button
            onClick={handleAdvanceToDrawer}
            className="flex-1 bg-secondary hover:bg-secondary/95 text-secondary-foreground h-12 px-6 rounded-lg font-semibold transition-colors flex items-center justify-between clickable-button"
          >
            <span className="text-white text-sm font-bold">
              {isClubRedeem ? 'Trocar por pontos' : isEditMode ? 'Salvar' : 'Adicionar'}
              {wholesaleFamily && !isClubRedeem ? ` (${watch('quantity')})` : ''}
            </span>
            <span className="text-white text-sm font-bold">
              {isClubRedeem
                ? `${clubRedeemPoints} pts`
                : formatPrice((wholesaleTotalPrice ?? totalPrice).toString())}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
